import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FiUser, FiMail, FiLock, FiTrendingUp, FiStar, FiCheck, FiHash, FiPlus, FiMessageSquare, FiCopy, FiX, FiAlertTriangle, FiHome, FiPhone, FiMapPin, FiChevronLeft, FiUsers, FiSearch, FiFileText, FiTrash2, FiDatabase, FiRefreshCw, FiEye, FiGlobe, } from 'react-icons/fi';
import { useAuth } from '../auth/AuthContext';
import { ROUTES } from '../../app/routes';
import FilePreviewModal from '../../components/ui/FilePreviewModal';
import { getApiKeys, generateApiKey, revokeApiKey, type SavedApiKey } from '../../services/apiKeyService';
import { listAgencyMemoryDocuments, uploadAgencyMemoryDocument, updateAgencyMemoryDocument, deleteAgencyMemoryDocument, } from '../claudeagents/claudeAgentApi';
import AllowedDomainsTab from './AllowedDomainsTab';
import '../../styles/Profile.css';
interface ProfileFormData {
    name: string;
    email: string;
    phone: string;
    company: string;
    role: string;
}
interface PasswordFormData {
    currentPassword: string;
    newPassword: string;
    confirmPassword: string;
}
interface AgencyFormData {
    agencyName: string;
    primaryContactName: string;
    email: string;
    phone: string;
    fax: string;
    cellPhone: string;
    addressLine1: string;
    addressLine2: string;
    city: string;
    state: string;
    zipCode: string;
    licenseNumber: string;
    domesticLicenseState: string;
}
interface Agent {
    id: string;
    userId: string;
    firstName: string;
    lastName: string;
    email: string;
    isSubscribed: boolean;
}
const PROFILE_TABS = ['profile', 'security', 'api-keys', 'agency', 'agents', 'agency-docs', 'allowed-domains'] as const;
type ProfileTab = typeof PROFILE_TABS[number];
interface ProfilePageProps {
    onOpenCreateApiKey?: () => void;
    refreshApiKeysTrigger?: number;
}
function getJwtClaim(token: string, claim: string): string {
    try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return (payload[claim] ?? '').toString();
    }
    catch {
        return '';
    }
}
const ProfilePage: React.FC<ProfilePageProps> = ({ onOpenCreateApiKey, refreshApiKeysTrigger }) => {
    const { user, isAuthenticated } = useAuth();
    const navigate = useNavigate();
    const isSuperUser = getJwtClaim(localStorage.getItem('authToken') || '', 'IsAgencyOwner').toLowerCase() === 'true';
    const [agents, setAgents] = useState<Agent[]>([]);
    const [agentsLoading, setAgentsLoading] = useState(false);
    const [agentsError, setAgentsError] = useState<string | null>(null);
    const [agentSearch, setAgentSearch] = useState('');
    // ?tab=<name> opens that tab (e.g. Settings > Allowed Domains links to ?tab=allowed-domains).
    const [searchParams] = useSearchParams();
    const [activeTab, setActiveTab] = useState<ProfileTab>(() => {
        const requested = searchParams.get('tab') as ProfileTab | null;
        if (!requested || !PROFILE_TABS.includes(requested) || (requested === 'agents' && !isSuperUser))
            return 'profile';
        return requested;
    });
    const [isSaving, setIsSaving] = useState(false);
    const [saveSuccess, setSaveSuccess] = useState(false);
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [apiKeys, setApiKeys] = useState<SavedApiKey[]>([]);
    const [isLoadingKeys, setIsLoadingKeys] = useState(false);
    const [isLoadingAgency, setIsLoadingAgency] = useState(false);
    const [keyError, setKeyError] = useState<string | null>(null);
    const [modalError, setModalError] = useState<string | null>(null);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [newKeyName, setNewKeyName] = useState('');
    const [createdKey, setCreatedKey] = useState('');
    const [createLoading, setCreateLoading] = useState(false);
    const [revokeConfirmId, setRevokeConfirmId] = useState<string | null>(null);
    const [isCopied, setIsCopied] = useState(false);
    const [docSelectedFiles, setDocSelectedFiles] = useState<File[]>([]);
    const [isDragOver, setIsDragOver] = useState(false);
    const [docUploading, setDocUploading] = useState(false);
    const [docUploadProgress, setDocUploadProgress] = useState<{
        done: number;
        total: number;
    } | null>(null);
    const [docUploadSuccess, setDocUploadSuccess] = useState(false);
    const [docUploadError, setDocUploadError] = useState<string | null>(null);
    const [docUploadToast, setDocUploadToast] = useState<string | null>(null);
    const [uploadedDocs, setUploadedDocs] = useState<{
        fileUrl?: string;
        fileName: string;
        transactionId?: string;
        created?: string;
        // Uploader's name (agency-wide listing shows everyone's documents).
        uploadedBy?: string;
        // True when the signed-in user uploaded it: only the uploader can send
        // it to memory, re-sync / remove it, or delete it (server-enforced).
        // Also true when the API did not say (older API without isOwner), so a
        // missing field keeps the old behaviour instead of hiding every action.
        isOwner?: boolean;
        // True only when the API confirmed the signed-in user is the uploader.
        ownerConfirmed?: boolean;
    }[]>([]);
    const [docsLoading, setDocsLoading] = useState(false);
    const [viewDoc, setViewDoc] = useState<{
        fileUrl?: string;
        fileName: string;
    } | null>(null);
    const [docViewingId, setDocViewingId] = useState<string | null>(null);
    const [docDeleteConfirmId, setDocDeleteConfirmId] = useState<string | null>(null);
    const [docDeletingId, setDocDeletingId] = useState<string | null>(null);
    const [docDeleteToast, setDocDeleteToast] = useState<string | null>(null);
    const [docDeleteError, setDocDeleteError] = useState<string | null>(null);
    const [memoryFileNames, setMemoryFileNames] = useState<Set<string>>(new Set());
    const [memoryActionId, setMemoryActionId] = useState<string | null>(null);
    const [selectedDocIds, setSelectedDocIds] = useState<Set<string>>(new Set());
    const [bulkMemoryProgress, setBulkMemoryProgress] = useState<{
        done: number;
        total: number;
    } | null>(null);
    const [docMemoryToast, setDocMemoryToast] = useState<string | null>(null);
    const [docMemoryError, setDocMemoryError] = useState<string | null>(null);
    const docInputRef = useRef<HTMLInputElement>(null);
    const memoryPathForFileName = (fileName: string) => {
        const stem = fileName.replace(/\.[^./]+$/, '');
        return `/agency-documents/${stem}.md`;
    };
    const fetchAgencyDocs = useCallback(async () => {
        const authToken = localStorage.getItem('authToken') || '';
        setDocsLoading(true);
        try {
            const res = await fetch(`${(process.env.REACT_APP_Auth_Url)}/api/momentumamp/insurance-agency-files`, {
                headers: { Authorization: `Bearer ${authToken}` },
            });
            if (!res.ok)
                throw new Error();
            const data = await res.json().catch(() => null);
            if (Array.isArray(data)) {
                // Agency-wide: every document anyone at the agency uploaded. isOwner
                // comes from the API (uploader's UserId vs the caller).
                setUploadedDocs(data
                    .filter((item: any) => item.channelId === 0)
                    .map((item: any) => ({
                    fileName: item.fileName,
                    transactionId: item.transactionId,
                    created: item.created,
                    // uploadedBy (API) falls back to FullName / WorkEmail / Username when the user
                    // row has blank names; for the caller's own rows use the signed-in name.
                    uploadedBy: item.uploadedBy
                        || [item.firstName, item.lastName].filter(Boolean).join(' ')
                        || (item.isOwner === true ? [localStorage.getItem('firstName'), localStorage.getItem('lastName')].filter(Boolean).join(' ') : '')
                        || undefined,
                    isOwner: typeof item.isOwner === 'boolean' ? item.isOwner : true,
                    ownerConfirmed: item.isOwner === true,
                })));
            }
        }
        catch {
        }
        finally {
            setDocsLoading(false);
        }
        try {
            const memoryDocs = await listAgencyMemoryDocuments();
            const titles = new Set(memoryDocs.map(d => (d.title || '').trim()).filter(Boolean));
            setMemoryFileNames(titles);
        }
        catch {
        }
    }, []);
    const fetchAgencyFileUrl = async (transactionId: string, fileName: string): Promise<string | null> => {
        const authToken = localStorage.getItem('authToken') || '';
        const params = new URLSearchParams({ transactionId, channelId: '', fileName });
        const res = await fetch(`${(process.env.REACT_APP_Auth_Url)}/api/momentumamp/insurance-agency-file?${params.toString()}`, {
            headers: { Authorization: `Bearer ${authToken}` },
        });
        if (!res.ok)
            throw new Error();
        const json = await res.json();
        return json?.fileUrl || null;
    };
    const viewAgencyDoc = async (transactionId: string, fileName: string) => {
        setDocViewingId(transactionId);
        try {
            const fileUrl = await fetchAgencyFileUrl(transactionId, fileName);
            if (fileUrl)
                setViewDoc({ fileUrl, fileName });
        }
        catch {
        }
        finally {
            setDocViewingId(null);
        }
    };
    const sendDocToMemory = async (transactionId: string, fileName: string) => {
        const fileUrl = await fetchAgencyFileUrl(transactionId, fileName);
        if (!fileUrl)
            throw new Error('Could not locate the file to send');
        await uploadAgencyMemoryDocument({ fileUrl, title: fileName, path: memoryPathForFileName(fileName), transactionId });
    };
    const handleSendToMemory = async (transactionId: string, fileName: string) => {
        setMemoryActionId(transactionId);
        try {
            await sendDocToMemory(transactionId, fileName);
            setMemoryFileNames(prev => new Set(prev).add(fileName));
            setDocMemoryToast(`"${fileName}" added to memory`);
            setTimeout(() => setDocMemoryToast(null), 3500);
        }
        catch (err: any) {
            setDocMemoryError(err.message || 'Failed to add to memory');
            setTimeout(() => setDocMemoryError(null), 4000);
        }
        finally {
            setMemoryActionId(null);
        }
    };
    const handleResyncMemory = async (transactionId: string, fileName: string) => {
        setMemoryActionId(transactionId);
        try {
            const fileUrl = await fetchAgencyFileUrl(transactionId, fileName);
            if (!fileUrl)
                throw new Error('Could not locate the file to sync');
            await updateAgencyMemoryDocument({ path: memoryPathForFileName(fileName), file_url: fileUrl, title: fileName, transaction_id: transactionId });
            setDocMemoryToast(`"${fileName}" re-synced to memory`);
            setTimeout(() => setDocMemoryToast(null), 3500);
        }
        catch (err: any) {
            setDocMemoryError(err.message || 'Failed to re-sync memory');
            setTimeout(() => setDocMemoryError(null), 4000);
        }
        finally {
            setMemoryActionId(null);
        }
    };
    const toggleDocSelected = (transactionId: string) => {
        setSelectedDocIds(prev => {
            const next = new Set(prev);
            if (next.has(transactionId))
                next.delete(transactionId);
            else
                next.add(transactionId);
            return next;
        });
    };
    const toggleSelectAllDocs = () => {
        const sendable = uploadedDocs.filter(d => d.transactionId && d.isOwner && !memoryFileNames.has(d.fileName));
        setSelectedDocIds(prev => prev.size === sendable.length && sendable.every(d => prev.has(d.transactionId!))
            ? new Set()
            : new Set(sendable.map(d => d.transactionId!)));
    };
    const handleBulkSendToMemory = async () => {
        const targets = uploadedDocs.filter(d => d.transactionId && selectedDocIds.has(d.transactionId));
        if (targets.length === 0)
            return;
        setBulkMemoryProgress({ done: 0, total: targets.length });
        const failed: string[] = [];
        for (let i = 0; i < targets.length; i++) {
            const doc = targets[i];
            try {
                await sendDocToMemory(doc.transactionId!, doc.fileName);
                setMemoryFileNames(prev => new Set(prev).add(doc.fileName));
            }
            catch {
                failed.push(doc.fileName);
            }
            setBulkMemoryProgress({ done: i + 1, total: targets.length });
        }
        setBulkMemoryProgress(null);
        setSelectedDocIds(new Set());
        if (failed.length > 0) {
            setDocMemoryError(`Failed to add to memory: ${failed.join(', ')}`);
            setTimeout(() => setDocMemoryError(null), 4000);
        }
        else {
            setDocMemoryToast(`${targets.length} document${targets.length > 1 ? 's' : ''} added to memory`);
            setTimeout(() => setDocMemoryToast(null), 3500);
        }
    };
    const handleRemoveFromMemory = async (transactionId: string, fileName: string) => {
        setMemoryActionId(transactionId);
        try {
            await deleteAgencyMemoryDocument(memoryPathForFileName(fileName), transactionId);
            setMemoryFileNames(prev => {
                const next = new Set(prev);
                next.delete(fileName);
                return next;
            });
            setDocMemoryToast(`"${fileName}" removed from memory`);
            setTimeout(() => setDocMemoryToast(null), 3500);
        }
        catch (err: any) {
            setDocMemoryError(err.message || 'Failed to remove from memory');
            setTimeout(() => setDocMemoryError(null), 4000);
        }
        finally {
            setMemoryActionId(null);
        }
    };
    const deleteDoc = async (transactionId: string, fileName?: string) => {
        const authToken = localStorage.getItem('authToken') || '';
        setDocDeleteConfirmId(null);
        setDocDeletingId(transactionId);
        try {
            const formData = new FormData();
            formData.append('transactionId', transactionId);
            const res = await fetch(`${(process.env.REACT_APP_Auth_Url)}/api/momentumamp/insurance-agency-file`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${authToken}` },
                body: formData,
            });
            if (!res.ok) {
                const json = await res.json().catch(() => null);
                const msg = json?.errorMessage || `Delete failed (${res.status})`;
                setDocDeleteError(msg);
                setTimeout(() => setDocDeleteError(null), 4000);
                return;
            }
            setUploadedDocs(prev => prev.filter(d => d.transactionId !== transactionId));
            setSelectedDocIds(prev => {
                if (!prev.has(transactionId))
                    return prev;
                const next = new Set(prev);
                next.delete(transactionId);
                return next;
            });
            if (fileName)
                deleteAgencyMemoryDocument(memoryPathForFileName(fileName), transactionId).catch(() => { });
            setMemoryFileNames(prev => {
                if (!fileName || !prev.has(fileName))
                    return prev;
                const next = new Set(prev);
                next.delete(fileName);
                return next;
            });
            const label = fileName ? `"${fileName}" deleted` : 'Document deleted';
            setDocDeleteToast(label);
            setTimeout(() => setDocDeleteToast(null), 3500);
        }
        catch {
            fetchAgencyDocs();
        }
        finally {
            setDocDeletingId(null);
        }
    };
    useEffect(() => {
        if (activeTab === 'api-keys')
            fetchKeys();
        if (activeTab === 'agency')
            fetchAgencyDetails();
        if (activeTab === 'agents')
            fetchAgents();
        if (activeTab === 'agency-docs')
            fetchAgencyDocs();
    }, [activeTab]);
    useEffect(() => {
        if (refreshApiKeysTrigger && activeTab === 'api-keys') {
            fetchKeys();
        }
    }, [refreshApiKeysTrigger]);
    const fetchAgents = async () => {
        const authToken = localStorage.getItem('authToken') || '';
        const amsToken = localStorage.getItem('amsToken') || '';
        if (!amsToken)
            return;
        const agencyId = getJwtClaim(authToken, 'NowCertsAgencyId');
        setAgentsLoading(true);
        setAgentsError(null);
        try {
            const res = await fetch(`${(process.env.REACT_APP_Auth_Url)}/api/account/agentlist?nctoken=${encodeURIComponent(amsToken)}&agencyId=${encodeURIComponent(agencyId)}`, { headers: { Authorization: `Bearer ${authToken}` } });
            if (!res.ok)
                throw new Error(`Failed to load agents (${res.status})`);
            const data = await res.json();
            setAgents(Array.isArray(data) ? data : (data.value ?? data.data ?? []));
        }
        catch (err: any) {
            setAgentsError(err.message || 'Failed to load agents');
        }
        finally {
            setAgentsLoading(false);
        }
    };
    const fetchKeys = async () => {
        setIsLoadingKeys(true);
        setKeyError(null);
        try {
            const keys = await getApiKeys();
            setApiKeys(keys);
        }
        catch (err: any) {
            setKeyError(err.message || 'Failed to load API keys');
        }
        finally {
            setIsLoadingKeys(false);
        }
    };
    const fetchAgencyDetails = async () => {
        const authToken = localStorage.getItem('authToken') || '';
        const amsToken = localStorage.getItem('amsToken') || '';
        if (!amsToken)
            return;
        setIsLoadingAgency(true);
        try {
            const res = await fetch(`${(process.env.REACT_APP_Auth_Url)}/api/account/agencydetails?nctoken=${encodeURIComponent(amsToken)}`, { headers: { Authorization: `Bearer ${authToken}` } });
            if (!res.ok)
                return;
            const data = await res.json();
            setAgencyData({
                agencyName: data.name || '',
                primaryContactName: data.primaryContact || '',
                email: data.email || '',
                phone: data.phone || '',
                fax: data.fax || '',
                cellPhone: data.cellPhone || '',
                addressLine1: data.addressLine1 || '',
                addressLine2: data.addressLine2 || '',
                city: data.city || '',
                state: data.state || '',
                zipCode: data.zipCode || '',
                licenseNumber: data.licenseNumber || '',
                domesticLicenseState: data.domesticLicenseState || '',
            });
        }
        catch {
        }
        finally {
            setIsLoadingAgency(false);
        }
    };
    const handleCreateKey = async () => {
        if (!newKeyName.trim())
            return;
        setCreateLoading(true);
        setModalError(null);
        try {
            const response = await generateApiKey(newKeyName.trim());
            if (response.data?.apiKey) {
                setCreatedKey(response.data.apiKey);
                await fetchKeys();
            }
            else {
                setModalError('Unable to generate API key. Please try again.');
            }
        }
        catch {
            setModalError('Something went wrong. Please try again after sometime.');
        }
        finally {
            setCreateLoading(false);
        }
    };
    const handleCloseCreateModal = () => {
        setShowCreateModal(false);
        setCreatedKey('');
        setNewKeyName('');
        setCreateLoading(false);
        setModalError(null);
    };
    const handleCopyKey = async () => {
        try {
            await navigator.clipboard.writeText(createdKey);
            setIsCopied(true);
            setTimeout(() => setIsCopied(false), 2000);
        }
        catch {
        }
    };
    const handleRevoke = async (apiKeyId: string) => {
        try {
            await revokeApiKey(apiKeyId);
            await fetchKeys();
        }
        catch (err: any) {
            setKeyError(err.message || 'Failed to revoke key');
        }
        setRevokeConfirmId(null);
    };
    const handleDocFiles = useCallback((files: FileList | null) => {
        if (!files || files.length === 0)
            return;
        const nextFiles = Array.from(files);
        setDocSelectedFiles(prev => [...prev, ...nextFiles]);
        setDocUploadError(null);
    }, []);
    const removeSelectedDocFile = (index: number) => {
        setDocSelectedFiles(prev => prev.filter((_, i) => i !== index));
    };
    const formatFileSize = (bytes: number): string => {
        if (bytes < 1024)
            return `${bytes} B`;
        if (bytes < 1024 * 1024)
            return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    };
    const handleDocUpload = async () => {
        if (docSelectedFiles.length === 0)
            return;
        const authToken = localStorage.getItem('authToken') || '';
        setDocUploading(true);
        setDocUploadError(null);
        setDocUploadSuccess(false);
        setDocUploadProgress({ done: 0, total: docSelectedFiles.length });
        const failed: string[] = [];
        for (let i = 0; i < docSelectedFiles.length; i++) {
            const file = docSelectedFiles[i];
            const transactionId = crypto.randomUUID();
            try {
                const formData = new FormData();
                formData.append('transactionId', transactionId);
                formData.append('formFile', file);
                const res = await fetch(`${(process.env.REACT_APP_Auth_Url)}/api/momentumamp/insurance-agency-file`, {
                    method: 'POST',
                    headers: { Authorization: `Bearer ${authToken}` },
                    body: formData,
                });
                if (!res.ok)
                    throw new Error(`Upload failed (${res.status})`);
            }
            catch {
                failed.push(file.name);
            }
            setDocUploadProgress({ done: i + 1, total: docSelectedFiles.length });
        }
        const attempted = docSelectedFiles.length;
        setDocUploading(false);
        setDocUploadProgress(null);
        setDocSelectedFiles([]);
        fetchAgencyDocs();
        if (failed.length > 0) {
            const msg = `Failed to upload: ${failed.join(', ')}`;
            setDocUploadError(msg);
            setTimeout(() => setDocUploadError(null), 5000);
        }
        const succeeded = attempted - failed.length;
        if (succeeded > 0) {
            setDocUploadSuccess(true);
            setTimeout(() => setDocUploadSuccess(false), 3000);
            setDocUploadToast(`${succeeded} document${succeeded > 1 ? 's' : ''} uploaded`);
            setTimeout(() => setDocUploadToast(null), 3500);
        }
    };
    const [profileData, setProfileData] = useState<ProfileFormData>({
        name: [localStorage.getItem('firstName'), localStorage.getItem('lastName')].filter(Boolean).join(' ') || user?.name || '',
        email: user?.email || localStorage.getItem('userName') || '',
        phone: '',
        company: localStorage.getItem('agency') || '',
        role: user?.role || 'Agent',
    });
    const [passwordData, setPasswordData] = useState<PasswordFormData>({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
    });
    const [agencyData, setAgencyData] = useState<AgencyFormData>({
        agencyName: '',
        primaryContactName: '',
        email: user?.email || '',
        phone: '',
        fax: '',
        cellPhone: '',
        addressLine1: '',
        addressLine2: '',
        city: '',
        state: '',
        zipCode: '',
        licenseNumber: '',
        domesticLicenseState: '',
    });
    const [agencyErrors, setAgencyErrors] = useState<Partial<Record<keyof AgencyFormData, string>>>({});
    const validateAgencyField = (field: keyof AgencyFormData, value: string): string => {
        const phoneRegex = /^[\d\s().+-]{7,20}$/;
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        switch (field) {
            case 'agencyName': return value.trim() ? '' : 'Agency name is required';
            case 'primaryContactName': return value.trim() ? '' : 'Primary contact name is required';
            case 'email': return !value.trim() ? 'Email is required' : !emailRegex.test(value) ? 'Invalid email address' : '';
            case 'phone': return !value.trim() ? 'Phone is required' : !phoneRegex.test(value) ? 'Invalid phone number' : '';
            case 'fax': return value && !phoneRegex.test(value) ? 'Invalid fax number' : '';
            case 'cellPhone': return value && !phoneRegex.test(value) ? 'Invalid cell phone number' : '';
            case 'addressLine1': return value.trim() ? '' : 'Address is required';
            case 'city': return value.trim() ? '' : 'City is required';
            case 'state': return !value.trim() ? 'State is required' : value.trim().length > 2 ? 'Use 2-letter state code (e.g. CA)' : '';
            case 'zipCode': return !value.trim() ? 'Zip code is required' : !/^\d{5}(-\d{4})?$/.test(value.trim()) ? 'Invalid zip code' : '';
            case 'licenseNumber': return value.trim() ? '' : 'License number is required';
            case 'domesticLicenseState': return !value.trim() ? 'License state is required' : value.trim().length > 2 ? 'Use 2-letter state code (e.g. CA)' : '';
            default: return '';
        }
    };
    const handleAgencyChange = (field: keyof AgencyFormData, value: string) => {
        setAgencyData(prev => ({ ...prev, [field]: value }));
        const fieldError = validateAgencyField(field, value);
        setAgencyErrors(prev => ({ ...prev, [field]: fieldError }));
        setError(null);
    };
    const handleSaveAgency = async (e: React.FormEvent) => {
        e.preventDefault();
        const newErrors: Partial<Record<keyof AgencyFormData, string>> = {};
        (Object.keys(agencyData) as (keyof AgencyFormData)[]).forEach(field => {
            const err = validateAgencyField(field, agencyData[field]);
            if (err)
                newErrors[field] = err;
        });
        if (Object.keys(newErrors).length > 0) {
            setAgencyErrors(newErrors);
            return;
        }
        setIsSaving(true);
        setError(null);
        try {
            const authToken = localStorage.getItem('authToken') || '';
            const res = await fetch(`${(process.env.REACT_APP_Auth_Url)}/api/account/updateagency`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${authToken}`,
                },
                body: JSON.stringify({
                    PrimaryContactName: agencyData.primaryContactName,
                    AddressLine1: agencyData.addressLine1,
                    AddressLine2: agencyData.addressLine2,
                    Phone: agencyData.phone,
                    Fax: agencyData.fax,
                    ZipCode: agencyData.zipCode,
                    StateName: agencyData.state,
                    City: agencyData.city,
                    LicenseNumber: agencyData.licenseNumber,
                    DomesticLicenseStateName: agencyData.domesticLicenseState,
                    WebsiteURL: '',
                    CellPhone: agencyData.cellPhone,
                    StripeCustomerId: ''
                }),
            });
            if (!res.ok) {
                const data = await res.json().catch(() => null);
                throw new Error(data?.message || `Failed to update agency (${res.status})`);
            }
            setSaveSuccess(true);
            setTimeout(() => setSaveSuccess(false), 3000);
        }
        catch (err: any) {
            setError(err.message || 'Failed to update agency information');
        }
        finally {
            setIsSaving(false);
        }
    };
    const handleProfileChange = (field: keyof ProfileFormData, value: string) => {
        setProfileData(prev => ({ ...prev, [field]: value }));
        setError(null);
    };
    const handlePasswordChange = (field: keyof PasswordFormData, value: string) => {
        setPasswordData(prev => ({ ...prev, [field]: value }));
        setError(null);
    };
    const handleSaveProfile = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSaving(true);
        setError(null);
        try {
            await new Promise(resolve => setTimeout(resolve, 1000));
            setSaveSuccess(true);
            setTimeout(() => setSaveSuccess(false), 3000);
        }
        catch (err: any) {
            setError(err.message || 'Failed to update profile');
        }
        finally {
            setIsSaving(false);
        }
    };
    const handleChangePassword = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        if (passwordData.newPassword !== passwordData.confirmPassword) {
            setError('New passwords do not match');
            return;
        }
        if (passwordData.newPassword.length < 8) {
            setError('Password must be at least 8 characters');
            return;
        }
        setIsSaving(true);
        try {
            const authToken = localStorage.getItem('authToken') || '';
            const res = await fetch(`${(process.env.REACT_APP_Auth_Url)}/api/account/changepassword`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${authToken}`,
                },
                body: JSON.stringify({
                    OldPassword: passwordData.currentPassword,
                    NewPassword: passwordData.newPassword,
                    ConfirmPassword: passwordData.confirmPassword,
                }),
            });
            const data = await res.json().catch(() => null);
            if (!res.ok || data?.status !== 1) {
                throw new Error(data?.message || `Failed to change password (${res.status})`);
            }
            setSaveSuccess(true);
            setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
            setTimeout(() => setSaveSuccess(false), 3000);
        }
        catch (err: any) {
            setError(err.message || 'Failed to change password');
        }
        finally {
            setIsSaving(false);
        }
    };
    if (!isAuthenticated) {
        return (<div className="profile-page">
        <div className="profile-guest glass-card">
          <FiUser size={48}/>
          <h2>Sign In Required</h2>
          <p>Please sign in to access your profile settings</p>
          <button className="glass-btn glass-btn-primary" onClick={() => navigate(ROUTES.LOGIN)}>
            Sign In
          </button>
        </div>
      </div>);
    }
    return (<div className="profile-page">
      <motion.div className="profile-container" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
        
        <div className="profile-hero-card">
          <div className="profile-hero">
            <button className="integrations-back-btn integrations-back-btn--inline" onClick={() => navigate(ROUTES.SETTINGS)}>
              <FiChevronLeft size={13}/> Back
            </button>
            <div className="profile-avatar">
              <span className="profile-avatar__initial">
                {profileData.name.charAt(0) || 'U'}
              </span>
            </div>
            <div className="profile-hero__info">
              <h2 className="profile-hero__name">{profileData.name || 'User'}</h2>
              <div className="profile-hero__meta">
                <span className="profile-hero__badge">
                  <span className="profile-hero__badge-dot"/>
                  {profileData.role || 'Agent'}
                </span>
                <span className="profile-hero__email">{profileData.email}</span>
              </div>
            </div>
          </div>

          <div className="profile-tabs">
            <button className={`profile-tab ${activeTab === 'profile' ? 'profile-tab--active' : ''}`} onClick={() => setActiveTab('profile')}>
              <FiUser size={14}/>Profile
            </button>
            <button className={`profile-tab ${activeTab === 'agency' ? 'profile-tab--active' : ''}`} onClick={() => setActiveTab('agency')}>
              <FiHome size={14}/>Agency
            </button>
            
            <button className={`profile-tab ${activeTab === 'api-keys' ? 'profile-tab--active' : ''}`} onClick={() => setActiveTab('api-keys')}>
              <FiHash size={14}/>MCP Keys
            </button>
            {isSuperUser && (<button className={`profile-tab ${activeTab === 'agents' ? 'profile-tab--active' : ''}`} onClick={() => setActiveTab('agents')}>
                <FiUsers size={14}/>Agents
              </button>)}
            <button className={`profile-tab ${activeTab === 'agency-docs' ? 'profile-tab--active' : ''}`} onClick={() => setActiveTab('agency-docs')}>
              <FiFileText size={14}/>Agency Documents
            </button>
            <button className={`profile-tab ${activeTab === 'allowed-domains' ? 'profile-tab--active' : ''}`} onClick={() => setActiveTab('allowed-domains')}>
              <FiGlobe size={14}/>Allowed Domains
            </button>
          </div>
        </div>

        <div className="profile-tab-content">
        {activeTab === 'profile' && (<motion.form className="profile-form" onSubmit={handleSaveProfile} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }}>
            <div className="profile-form__header">
              <div className="profile-form__header-icon"><FiUser size={16}/></div>
              <div>
                <div className="profile-form__title">Personal Information</div>
                <div className="profile-form__desc">Update your name, email and contact details</div>
              </div>
            </div>

            <div className="profile-form__grid">
              <div className="profile-field">
                <label className="profile-field__label">Full Name</label>
                <div className="profile-field__input-wrap">
                  <FiUser className="profile-field__icon" size={16}/>
                  <input type="text" className="glass-input profile-field__input" placeholder="Your full name" value={profileData.name} onChange={(e) => handleProfileChange('name', e.target.value)} required/>
                </div>
              </div>

              <div className="profile-field">
                <label className="profile-field__label">Email Address</label>
                <div className="profile-field__input-wrap">
                  <FiMail className="profile-field__icon" size={16}/>
                  <input type="email" className="glass-input profile-field__input" placeholder="your@email.com" value={profileData.email} onChange={(e) => handleProfileChange('email', e.target.value)} required/>
                </div>
              </div>

              <div className="profile-field">
                <label className="profile-field__label">Phone Number</label>
                <div className="profile-field__input-wrap">
                  <FiMessageSquare className="profile-field__icon" size={16}/>
                  <input type="tel" className="glass-input profile-field__input" placeholder="+1 (555) 000-0000" value={profileData.phone} onChange={(e) => handleProfileChange('phone', e.target.value)}/>
                </div>
              </div>

              <div className="profile-field">
                <label className="profile-field__label">Company</label>
                <div className="profile-field__input-wrap">
                  <FiTrendingUp className="profile-field__icon" size={16}/>
                  <input type="text" className="glass-input profile-field__input" placeholder="Your company name" value={profileData.company} onChange={(e) => handleProfileChange('company', e.target.value)}/>
                </div>
              </div>
            </div>

            <div className="profile-field profile-field--full">
              <label className="profile-field__label">Role / Title</label>
              <div className="profile-field__input-wrap">
                <FiTrendingUp className="profile-field__icon" size={16}/>
                <input type="text" className="glass-input profile-field__input" placeholder="Your role or job title" value={profileData.role} onChange={(e) => handleProfileChange('role', e.target.value)}/>
              </div>
            </div>

            {error && (<motion.p className="profile-error" initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }}>
                {error}
              </motion.p>)}

            
          </motion.form>)}

        {activeTab === 'security' && (<motion.form className="profile-form" onSubmit={handleChangePassword} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }}>
            <div className="profile-form__header">
              <div className="profile-form__header-icon"><FiLock size={16}/></div>
              <div>
                <div className="profile-form__title">Change Password</div>
                <div className="profile-form__desc">Update your password to keep your account secure</div>
              </div>
            </div>

            <div className="profile-field">
              <label className="profile-field__label">Current Password</label>
              <div className="profile-field__input-wrap">
                <FiLock className="profile-field__icon" size={16}/>
                <input type={showCurrentPassword ? 'text' : 'password'} className="glass-input profile-field__input" placeholder="Enter current password" value={passwordData.currentPassword} onChange={(e) => handlePasswordChange('currentPassword', e.target.value)} required/>
                <button type="button" className="profile-field__toggle" onClick={() => setShowCurrentPassword(!showCurrentPassword)}>
                  {showCurrentPassword ? <FiHash size={16}/> : <FiHash size={16}/>}
                </button>
              </div>
            </div>

            <div className="profile-field">
              <label className="profile-field__label">New Password</label>
              <div className="profile-field__input-wrap">
                <FiLock className="profile-field__icon" size={16}/>
                <input type={showNewPassword ? 'text' : 'password'} className="glass-input profile-field__input" placeholder="Enter new password" value={passwordData.newPassword} onChange={(e) => handlePasswordChange('newPassword', e.target.value)} required/>
                <button type="button" className="profile-field__toggle" onClick={() => setShowNewPassword(!showNewPassword)}>
                  {showNewPassword ? <FiHash size={16}/> : <FiHash size={16}/>}
                </button>
              </div>
              <span className="profile-field__hint">Must be at least 8 characters</span>
            </div>

            <div className="profile-field">
              <label className="profile-field__label">Confirm New Password</label>
              <div className="profile-field__input-wrap">
                <FiLock className="profile-field__icon" size={16}/>
                <input type={showConfirmPassword ? 'text' : 'password'} className="glass-input profile-field__input" placeholder="Confirm new password" value={passwordData.confirmPassword} onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)} required/>
                <button type="button" className="profile-field__toggle" onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                  {showConfirmPassword ? <FiHash size={16}/> : <FiHash size={16}/>}
                </button>
              </div>
            </div>

            {error && (<motion.p className="profile-error" initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }}>
                {error}
              </motion.p>)}

            <div className="profile-form__actions">
              <button type="submit" className="glass-btn glass-btn-primary profile-save-btn" disabled={isSaving}>
                {isSaving ? (<span className="profile-loader"/>) : saveSuccess ? (<>
                    <FiCheck size={16}/>
                    Password Changed!
                  </>) : (<>
                    <FiLock size={16}/>
                    Change Password
                  </>)}
              </button>
            </div>
          </motion.form>)}
        {activeTab === 'api-keys' && (<motion.div className="profile-form" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }}>
            <div className="api-keys-header">
              <div className="profile-page__header-left-row">
                <div className="profile-form__header-icon"><FiHash size={16}/></div>
                <div>
                  <div className="profile-form__title">MCP Keys</div>
                  <div className="profile-form__desc">Authenticate requests to the Momentum AI API</div>
                </div>
              </div>
              <div className="profile-page__api-keys-header-right">
                <button className="api-keys-create-btn" onClick={() => {
                if ((process.env.REACT_APP_MCP_Url))
                    window.open((process.env.REACT_APP_MCP_Url), '_blank', 'noopener,noreferrer');
            }}>
                  <FiHash size={14}/>
                  MCP Details
                </button>
                <button className="api-keys-create-btn" onClick={() => {
                if (onOpenCreateApiKey) {
                    onOpenCreateApiKey();
                }
                else {
                    setCreatedKey('');
                    setNewKeyName('');
                    setShowCreateModal(true);
                }
            }}>
                  <FiPlus size={14}/>
                  Create Key
                </button>
              </div>
            </div>

            
            {keyError && (<motion.p initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }} className="profile-error profile-page__error--mb-3">
                {keyError}
              </motion.p>)}
            {isLoadingKeys ? (<div className="api-key-skeleton-list">
                {Array.from({ length: 4 }).map((_, i) => (<div key={i} className="api-key-item api-key-item--skeleton">
                    <div className="api-key-item__left">
                      <div className="api-key-item__icon api-key-item__icon--skeleton"/>
                      <div className="api-key-item__info">
                        <div className="api-key-item__value-skeleton"/>
                        <div className="api-key-item__date-skeleton"/>
                      </div>
                    </div>
                    <div className="api-key-item__right">
                      <div className="api-key-item__action-skeleton"/>
                    </div>
                  </div>))}
              </div>) : apiKeys.filter(k => !k.isRevoked).length === 0 && apiKeys.filter(k => k.isRevoked).length === 0 ? (<div className="api-keys-empty">
                <div className="api-keys-empty__icon">
                  <FiLock size={22}/>
                </div>
                <p className="api-keys-empty__title">No API keys yet</p>
                <p className="api-keys-empty__desc">
                  Create a key to authenticate programmatic access to Insurance Agency AI.
                </p>
              </div>) : (<div>
                
                {apiKeys.filter(k => !k.isRevoked).length > 0 && (<div className="api-key-list">
                    {apiKeys.filter(k => !k.isRevoked).map((key) => (<div key={key.apiKeyId} className="api-key-item">
                        <div className="api-key-item__left">
                          <div className="api-key-item__icon">
                            <FiLock size={14}/>
                          </div>
                          <div className="api-key-item__info">
                            <div className="profile-page__key-value-row">
                              <span className="api-key-item__value">{key.generatedApiKey}</span>
                              <span className="api-key-item__desc">{key.description}</span>
                            </div>
                            <span className="api-key-item__date">
                              Created {new Date(key.created).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' })}
                            </span>
                          </div>
                        </div>
                        <div className="api-key-item__right">
                          {revokeConfirmId === key.apiKeyId ? (<div className="api-key-revoke-confirm">
                              <span className="api-key-revoke-confirm__label">Revoke?</span>
                              <button className="api-key-btn-confirm" onClick={() => handleRevoke(key.apiKeyId)}>Yes</button>
                              <button className="api-key-btn-cancel" onClick={() => setRevokeConfirmId(null)}>Cancel</button>
                            </div>) : (<button className="api-key-btn-revoke" onClick={() => setRevokeConfirmId(key.apiKeyId)}>
                              Revoke
                            </button>)}
                        </div>
                      </div>))}
                  </div>)}

                
                {apiKeys.filter(k => k.isRevoked).length > 0 && (<>
                    <div className="profile-page__divider-row">
                      <div className="profile-page__divider-line"/>
                      <span className="profile-page__divider-label">
                        Revoked Keys
                      </span>
                      <div className="profile-page__divider-line"/>
                    </div>
                    <div className="api-key-list">
                      {apiKeys.filter(k => k.isRevoked).map((key) => (<div key={key.apiKeyId} className="api-key-item api-key-item--revoked">
                          <div className="api-key-item__left">
                            <div className="api-key-item__icon api-key-item__icon--revoked">
                              <FiX size={13}/>
                            </div>
                            <div className="api-key-item__info">
                              <div className="profile-page__key-value-row">
                                <span className="api-key-item__value api-key-item__value--revoked">{key.generatedApiKey}</span>
                                <span className="api-key-item__desc api-key-item__desc--revoked">{key.description}</span>
                              </div>
                              <span className="api-key-item__date">
                                Created {new Date(key.created).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' })}
                              </span>
                            </div>
                          </div>
                          <div className="api-key-item__right">
                            <span className="api-key-item__revoked-badge">Revoked</span>
                          </div>
                        </div>))}
                    </div>
                  </>)}
              </div>)}
          </motion.div>)}

        {activeTab === 'agency' && (<motion.form className="profile-form" onSubmit={handleSaveAgency} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }}>
            <div className="profile-form__header">
              <div className="profile-form__header-icon"><FiHome size={16}/></div>
              <div>
                <div className="profile-form__title">Agency Information</div>
                <div className="profile-form__desc">Manage your agency details, address and license</div>
              </div>
            </div>

            {isLoadingAgency ? (<div className="profile-form__grid profile-form__grid--3col">
                {Array.from({ length: 13 }).map((_, i) => (<div key={i} className="profile-field">
                    <div className="profile-field__skeleton-label"/>
                    <div className="profile-field__skeleton-input"/>
                  </div>))}
              </div>) : (<div className="profile-form__grid profile-form__grid--3col">
              <div className="profile-field">
                <label className="profile-field__label">Agency Name <span className="profile-field__required">*</span></label>
                <div className="profile-field__input-wrap">
                  <FiHome className="profile-field__icon" size={16}/>
                  <input disabled type="text" className={`glass-input profile-field__input${agencyErrors.agencyName ? ' profile-field__input--error' : ''}`} placeholder="Your Agency Name" value={agencyData.agencyName} title='Agency Name cannot be updated'/> 
                </div>
                {agencyErrors.agencyName && <span className="profile-field__error">{agencyErrors.agencyName}</span>}
              </div>
              <div className="profile-field">
                <label className="profile-field__label">Primary Contact Name <span className="profile-field__required">*</span></label>
                <div className="profile-field__input-wrap">
                  <FiUser className="profile-field__icon" size={16}/>
                  <input type="text" className={`glass-input profile-field__input${agencyErrors.primaryContactName ? ' profile-field__input--error' : ''}`} placeholder="Contact Person Name" value={agencyData.primaryContactName} onChange={(e) => handleAgencyChange('primaryContactName', e.target.value)}/>
                </div>
                {agencyErrors.primaryContactName && <span className="profile-field__error">{agencyErrors.primaryContactName}</span>}
              </div>
              <div className="profile-field">
                <label className="profile-field__label">Email <span className="profile-field__required">*</span></label>
                <div className="profile-field__input-wrap">
                  <FiMail className="profile-field__icon" size={16}/>
                  <input disabled type="email" className={`glass-input profile-field__input${agencyErrors.email ? ' profile-field__input--error' : ''}`} placeholder="you@agency.com" value={agencyData.email} title='Email cannot be updated'/> 
                </div>
                {agencyErrors.email && <span className="profile-field__error">{agencyErrors.email}</span>}
              </div>
              <div className="profile-field">
                <label className="profile-field__label">Phone <span className="profile-field__required">*</span></label>
                <div className="profile-field__input-wrap">
                  <FiPhone className="profile-field__icon" size={16}/>
                  <input type="tel" className={`glass-input profile-field__input${agencyErrors.phone ? ' profile-field__input--error' : ''}`} placeholder="(555) 000-0000" value={agencyData.phone} onChange={(e) => handleAgencyChange('phone', e.target.value)}/>
                </div>
                {agencyErrors.phone && <span className="profile-field__error">{agencyErrors.phone}</span>}
              </div>
              <div className="profile-field">
                <label className="profile-field__label">Fax</label>
                <div className="profile-field__input-wrap">
                  <FiPhone className="profile-field__icon" size={16}/>
                  <input type="tel" className={`glass-input profile-field__input${agencyErrors.fax ? ' profile-field__input--error' : ''}`} placeholder="(555) 000-0000" value={agencyData.fax} onChange={(e) => handleAgencyChange('fax', e.target.value)}/>
                </div>
                {agencyErrors.fax && <span className="profile-field__error">{agencyErrors.fax}</span>}
              </div>
              <div className="profile-field">
                <label className="profile-field__label">Cell Phone</label>
                <div className="profile-field__input-wrap">
                  <FiPhone className="profile-field__icon" size={16}/>
                  <input type="tel" className={`glass-input profile-field__input${agencyErrors.cellPhone ? ' profile-field__input--error' : ''}`} placeholder="(555) 000-0000" value={agencyData.cellPhone} onChange={(e) => handleAgencyChange('cellPhone', e.target.value)}/>
                </div>
                {agencyErrors.cellPhone && <span className="profile-field__error">{agencyErrors.cellPhone}</span>}
              </div>
              <div className="profile-field">
                <label className="profile-field__label">Address Line 1 <span className="profile-field__required">*</span></label>
                <div className="profile-field__input-wrap">
                  <FiMapPin className="profile-field__icon" size={16}/>
                  <input type="text" className={`glass-input profile-field__input${agencyErrors.addressLine1 ? ' profile-field__input--error' : ''}`} placeholder="Street address" value={agencyData.addressLine1} onChange={(e) => handleAgencyChange('addressLine1', e.target.value)}/>
                </div>
                {agencyErrors.addressLine1 && <span className="profile-field__error">{agencyErrors.addressLine1}</span>}
              </div>
              <div className="profile-field">
                <label className="profile-field__label">Address Line 2</label>
                <div className="profile-field__input-wrap">
                  <FiMapPin className="profile-field__icon" size={16}/>
                  <input type="text" className="glass-input profile-field__input" placeholder="Suite, Building, etc." value={agencyData.addressLine2} onChange={(e) => handleAgencyChange('addressLine2', e.target.value)}/>
                </div>
              </div>
              <div className="profile-field">
                <label className="profile-field__label">City <span className="profile-field__required">*</span></label>
                <div className="profile-field__input-wrap">
                  <FiMapPin className="profile-field__icon" size={16}/>
                  <input type="text" className={`glass-input profile-field__input${agencyErrors.city ? ' profile-field__input--error' : ''}`} placeholder="City" value={agencyData.city} onChange={(e) => handleAgencyChange('city', e.target.value)}/>
                </div>
                {agencyErrors.city && <span className="profile-field__error">{agencyErrors.city}</span>}
              </div>
              <div className="profile-field">
                <label className="profile-field__label">State <span className="profile-field__required">*</span></label>
                <div className="profile-field__input-wrap">
                  <FiMapPin className="profile-field__icon" size={16}/>
                  <input type="text" className={`glass-input profile-field__input${agencyErrors.state ? ' profile-field__input--error' : ''}`} placeholder="e.g., CA" maxLength={2} value={agencyData.state} onChange={(e) => handleAgencyChange('state', e.target.value.toUpperCase())}/>
                </div>
                {agencyErrors.state && <span className="profile-field__error">{agencyErrors.state}</span>}
              </div>
              <div className="profile-field">
                <label className="profile-field__label">Zip Code <span className="profile-field__required">*</span></label>
                <div className="profile-field__input-wrap">
                  <FiMapPin className="profile-field__icon" size={16}/>
                  <input type="text" className={`glass-input profile-field__input${agencyErrors.zipCode ? ' profile-field__input--error' : ''}`} placeholder="00000" maxLength={10} value={agencyData.zipCode} onChange={(e) => handleAgencyChange('zipCode', e.target.value)}/>
                </div>
                {agencyErrors.zipCode && <span className="profile-field__error">{agencyErrors.zipCode}</span>}
              </div>
              <div className="profile-field">
                <label className="profile-field__label">License Number <span className="profile-field__required">*</span></label>
                <div className="profile-field__input-wrap">
                  <FiHome className="profile-field__icon" size={16}/>
                  <input type="text" className={`glass-input profile-field__input${agencyErrors.licenseNumber ? ' profile-field__input--error' : ''}`} placeholder="License Number" value={agencyData.licenseNumber} onChange={(e) => handleAgencyChange('licenseNumber', e.target.value)}/>
                </div>
                {agencyErrors.licenseNumber && <span className="profile-field__error">{agencyErrors.licenseNumber}</span>}
              </div>
              <div className="profile-field">
                <label className="profile-field__label">Domestic License State <span className="profile-field__required">*</span></label>
                <div className="profile-field__input-wrap">
                  <FiMapPin className="profile-field__icon" size={16}/>
                  <input type="text" className={`glass-input profile-field__input${agencyErrors.domesticLicenseState ? ' profile-field__input--error' : ''}`} placeholder="e.g., CA" maxLength={2} value={agencyData.domesticLicenseState} onChange={(e) => handleAgencyChange('domesticLicenseState', e.target.value.toUpperCase())}/>
                </div>
                {agencyErrors.domesticLicenseState && <span className="profile-field__error">{agencyErrors.domesticLicenseState}</span>}
              </div>
            </div>)}

            {error && (<motion.p className="profile-error" initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }}>
                {error}
              </motion.p>)}

            
          </motion.form>)}

        {activeTab === 'agents' && (<motion.div className="profile-form" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }}>
            <div className="profile-form__header profile-page__agents-header">
              <div className="profile-page__header-left-row">
                <div className="profile-form__header-icon"><FiUsers size={16}/></div>
                <div>
                  <div className="profile-form__title">Agents</div>
                  <div className="profile-form__desc">Agents associated with your agency</div>
                </div>
              </div>
              <div className="profile-page__search-wrap">
                <FiSearch size={14} style={{ position: 'absolute', left: 10, color: 'var(--text-muted)', pointerEvents: 'none' }}/>
                <input type="text" className="glass-input profile-page__agent-search-input" placeholder="Search by agent name..." value={agentSearch} onChange={e => setAgentSearch(e.target.value)}/>
              </div>
            </div>

            

            {agentsError && (<motion.p className="profile-error profile-page__error--mb-3" initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }}>
                {agentsError}
              </motion.p>)}

            {agentsLoading ? (<div className="api-key-skeleton-list">
                {Array.from({ length: 5 }).map((_, i) => (<div key={i} className="api-key-item api-key-item--skeleton">
                    <div className="api-key-item__left">
                      <div className="api-key-item__icon api-key-item__icon--skeleton"/>
                      <div className="api-key-item__info">
                        <div className="api-key-item__value-skeleton"/>
                        <div className="api-key-item__date-skeleton"/>
                      </div>
                    </div>
                    <div className="api-key-item__right">
                      <div className="api-key-item__toggle-skeleton"/>
                    </div>
                  </div>))}
              </div>) : (() => {
                const filtered = agents.filter(a => !agentSearch.trim() ||
                    `${a.firstName} ${a.lastName}`.toLowerCase().includes(agentSearch.toLowerCase()));
                return filtered.length === 0 ? (<div className="api-keys-empty">
                    <div className="api-keys-empty__icon"><FiUser size={22}/></div>
                    <p className="api-keys-empty__title">No agents found</p>
                    <p className="api-keys-empty__desc">
                      {agentSearch.trim() ? 'No agents match your search.' : 'No agents are associated with your agency.'}
                    </p>
                  </div>) : (<div className="api-key-list">
                    {filtered.map(agent => (<div key={agent.id} className="api-key-item">
                        <div className="api-key-item__left">
                          <div className="api-key-item__icon">
                            <FiUser size={14}/>
                          </div>
                          <div className="api-key-item__info">
                            <div className="profile-page__key-value-row">
                              <span className="api-key-item__value">{agent.firstName} {agent.lastName}</span>
                            </div>
                            <span className="api-key-item__date">{agent.email}</span>
                          </div>
                        </div>
                      </div>))}
                  </div>);
            })()}
          </motion.div>)}

        {activeTab === 'allowed-domains' && (<motion.div className="profile-form" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }}>
            <AllowedDomainsTab/>
          </motion.div>)}

        {activeTab === 'agency-docs' && (<motion.div className="profile-form" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }}>
            
            <div className="profile-form__header profile-page__docs-header">
              <div className="profile-form__header-icon"><FiFileText size={16}/></div>
              <div>
                <div className="profile-form__title">Agency Documents</div>
                <div className="profile-form__desc">Upload and manage agency documents, licenses, and certificates</div>
              </div>
            </div>

            <div className="profile-page__docs-divider"/>

            
            <input ref={docInputRef} type="file" multiple accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.md,.txt" className="profile-page__hidden-file-input" onChange={(e) => {
                const selectedFiles = e.target.files;
                handleDocFiles(selectedFiles);
                e.target.value = '';
            }}/>

            
            <div className={`agency-docs__upload-bar${isDragOver ? ' agency-docs__upload-bar--drag' : ''}`} onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }} onDragLeave={() => setIsDragOver(false)} onDrop={(e) => { e.preventDefault(); setIsDragOver(false); if (e.dataTransfer.files.length)
            handleDocFiles(e.dataTransfer.files); }}>
              <div className="agency-docs__upload-bar-left" onClick={() => docInputRef.current?.click()}>
                <div className="agency-docs__upload-bar-icon"><FiPlus size={16}/></div>
                {docSelectedFiles.length > 0 ? (<div className="agency-docs__upload-bar-file">
                    <span className="agency-docs__file-name agency-docs__file-name--clamped">
                      {docSelectedFiles.length === 1 ? docSelectedFiles[0].name : `${docSelectedFiles.length} files selected`}
                    </span>
                    <span className="agency-docs__file-size">
                      {formatFileSize(docSelectedFiles.reduce((sum, f) => sum + f.size, 0))}
                    </span>
                  </div>) : (<div className="agency-docs__upload-bar-hint">
                    <span className="agency-docs__upload-bar-cta">Click to browse</span>
                    <span className="agency-docs__file-size">or drag files here · multiple files supported</span>
                  </div>)}
              </div>
              <div className="agency-docs__upload-bar-right">
                {docSelectedFiles.length > 0 && (<button className="agency-docs__file-remove" type="button" title="Clear selection" onClick={() => { setDocSelectedFiles([]); setDocUploadError(null); }}>
                    <FiX size={14}/>
                  </button>)}
                <button className="glass-btn profile-page__upload-btn" type="button" disabled={docSelectedFiles.length === 0 || docUploading} onClick={handleDocUpload} style={{
                background: docSelectedFiles.length > 0 ? 'var(--gradient-purple)' : undefined,
                color: docSelectedFiles.length > 0 ? 'white' : 'var(--text-primary)',
                opacity: docSelectedFiles.length === 0 || docUploading ? 0.5 : 1,
            }}>
                  {docUploading
                ? <><span className="profile-loader profile-loader--sm"/> {docUploadProgress ? `Uploading ${docUploadProgress.done}/${docUploadProgress.total}…` : 'Uploading…'}</>
                : docUploadSuccess
                    ? <><FiCheck size={13}/> Uploaded!</>
                    : <><FiCheck size={13}/> Upload{docSelectedFiles.length > 1 ? ` (${docSelectedFiles.length})` : ''}</>}
                </button>
              </div>
            </div>

            
            {docSelectedFiles.length > 1 && (<div className="profile-page__docs-header" style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8, marginBottom: 0 }}>
                {docSelectedFiles.map((f, i) => (<span key={`${f.name}-${i}`} className="agency-docs__panel-count" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    {f.name}
                    <FiX size={11} style={{ cursor: 'pointer' }} onClick={() => removeSelectedDocFile(i)}/>
                  </span>))}
              </div>)}

            
            {docUploadError && (<motion.p className="profile-error profile-page__error--mt-2-mb-0" initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }}>
                {docUploadError}
              </motion.p>)}

            
            <div className="agency-docs__table-wrap">
              <div className="agency-docs__table-header">
                <span className="agency-docs__panel-title">Documents</span>
                {!docsLoading && <span className="agency-docs__panel-count">{uploadedDocs.length}</span>}
                {selectedDocIds.size > 0 && (<button type="button" className="glass-btn profile-page__purple-btn" style={{ marginLeft: 'auto', padding: '4px 12px', fontSize: 12 }} disabled={!!bulkMemoryProgress} onClick={handleBulkSendToMemory}>
                    {bulkMemoryProgress
                    ? <><span className="profile-loader profile-loader--xs"/> Sending {bulkMemoryProgress.done}/{bulkMemoryProgress.total}…</>
                    : <><FiDatabase size={12}/> Send {selectedDocIds.size} to Memory</>}
                  </button>)}
              </div>

              {docsLoading ? (<div className="agency-docs__table-skeleton">
                  <div className="agency-docs__table-cols">
                    <span>Name</span>
                    <span>Uploaded by</span>
                    <span>Uploaded</span>
                    <span />
                  </div>
                  <div className="agency-docs__skeleton-rows">
                    {Array.from({ length: 4 }).map((_, i) => (<div key={i} className="agency-docs__table-row agency-docs__table-row--skeleton">
                        <div className="agency-docs__table-row-name">
                          <div className="agency-docs__file-icon agency-docs__file-icon--skeleton"/>
                          <div className="agency-docs__cell agency-docs__cell--name"/>
                        </div>
                        <div className="agency-docs__cell agency-docs__cell--uploader"/>
                        <div className="agency-docs__cell agency-docs__cell--date"/>
                        <div className="profile-page__flex-end">
                          <div className="agency-docs__cell agency-docs__cell--action"/>
                        </div>
                      </div>))}
                  </div>
                </div>) : uploadedDocs.length === 0 ? (<div className="agency-docs__empty agency-docs__empty--tight">
                  <FiFileText size={26} style={{ opacity: 0.25 }}/>
                  <span>No documents uploaded yet</span>
                </div>) : (<>
                  <div className="agency-docs__table-cols">
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                      <input type="checkbox" title="Select all not yet in memory" style={{ width: 16, height: 16, cursor: 'pointer', accentColor: '#7c3aed', flexShrink: 0 }} checked={(() => {
                    const sendable = uploadedDocs.filter(d => d.transactionId && d.isOwner && !memoryFileNames.has(d.fileName));
                    return sendable.length > 0 && sendable.every(d => selectedDocIds.has(d.transactionId!));
                })()} onChange={toggleSelectAllDocs} disabled={uploadedDocs.every(d => !d.transactionId || !d.isOwner || memoryFileNames.has(d.fileName))}/>
                      Name
                    </span>
                    <span>Uploaded by</span>
                    <span>Uploaded</span>
                    <span />
                  </div>
                  <div className="agency-docs__scroll">
                    {uploadedDocs.map((doc, index) => (<div key={`${doc.transactionId ?? doc.fileName}-${index}`} className="agency-docs__table-row">
                        <div className="agency-docs__table-row-name">
                          {doc.transactionId && doc.isOwner && !memoryFileNames.has(doc.fileName) ? (<input type="checkbox" checked={selectedDocIds.has(doc.transactionId)} onChange={() => toggleDocSelected(doc.transactionId!)} title="Select for bulk Send to Memory" style={{ width: 16, height: 16, cursor: 'pointer', accentColor: '#7c3aed', flexShrink: 0 }}/>) : (<span style={{ width: 16, display: 'inline-block', flexShrink: 0 }}/>)}
                          <div className="agency-docs__file-icon agency-docs__file-icon--sm"><FiFileText size={14}/></div>
                          <span className="agency-docs__file-name">{doc.fileName}</span>
                        </div>
                        <span className="agency-docs__file-size agency-docs__file-size--noshrink agency-docs__uploader" title={doc.uploadedBy ? `Uploaded by ${doc.uploadedBy}${doc.ownerConfirmed ? ' (you)' : ''}` : undefined}>
                          {doc.uploadedBy ? (doc.ownerConfirmed ? `${doc.uploadedBy} (you)` : doc.uploadedBy) : (doc.ownerConfirmed ? 'You' : '—')}
                        </span>
                        <span className="agency-docs__file-size agency-docs__file-size--noshrink">
                          {doc.created ? new Date(doc.created).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '—'}
                        </span>
                        <div className="profile-page__file-actions">
                          {doc.transactionId && memoryFileNames.has(doc.fileName) && (<span className="agency-docs__memory-tag" title={doc.isOwner ? "This document is in Claude's memory" : `This document is in Claude's memory. Only its uploader${doc.uploadedBy ? ` (${doc.uploadedBy})` : ''} can re-sync or remove it.`}>
                              <FiDatabase size={10}/> Memory
                            </span>)}
                          {/* Send to Memory / Re-sync / Remove from Memory: uploader only (the backend rejects anyone else). */}
                          {doc.transactionId && doc.isOwner && (memoryFileNames.has(doc.fileName) ? (<>
                                <button type="button" className="agency-docs__memory-btn" disabled={memoryActionId === doc.transactionId} title="Re-send this document's current file to memory (overwrites the memory copy)" onClick={() => handleResyncMemory(doc.transactionId!, doc.fileName)}>
                                  {memoryActionId === doc.transactionId
                            ? <span className="profile-loader profile-loader--xs"/>
                            : <><FiRefreshCw size={11}/> Re-sync</>}
                                </button>
                                <button type="button" className="agency-docs__memory-btn" disabled={memoryActionId === doc.transactionId} title="Remove from Claude's memory — keeps the agency document" onClick={() => handleRemoveFromMemory(doc.transactionId!, doc.fileName)}>
                                  {memoryActionId === doc.transactionId
                            ? <span className="profile-loader profile-loader--xs"/>
                            : 'Remove from Memory'}
                                </button>
                              </>) : (<button type="button" className="agency-docs__memory-btn" disabled={memoryActionId === doc.transactionId} title="Add this document to Claude's memory" onClick={() => handleSendToMemory(doc.transactionId!, doc.fileName)}>
                                {memoryActionId === doc.transactionId
                            ? <span className="profile-loader profile-loader--xs"/>
                            : <><FiDatabase size={12}/> Send to Memory</>}
                              </button>))}
                          
                          {doc.transactionId && (<button type="button" className="agency-docs__file-view--icon" title="View" disabled={docViewingId === doc.transactionId} onClick={() => viewAgencyDoc(doc.transactionId!, doc.fileName)}>
                              {docViewingId === doc.transactionId
                            ? <span className="profile-loader profile-loader--xs"/>
                            : <FiEye size={13}/>}
                            </button>)}
                          {doc.transactionId && doc.isOwner && (<button type="button" className="agency-docs__file-delete" onClick={() => setDocDeleteConfirmId(doc.transactionId!)} title="Delete Document" disabled={docDeletingId === doc.transactionId}>
                              {docDeletingId === doc.transactionId
                            ? <span className="profile-loader profile-loader--sm"/>
                            : <FiTrash2 size={13}/>}
                            </button>)}
                        </div>
                      </div>))}
                  </div>
                </>)}
            </div>

          </motion.div>)}

        </div>

      </motion.div>

      
      {docUploadToast && (<motion.div initial={{ opacity: 0, y: -16, x: 16 }} animate={{ opacity: 1, y: 0, x: 0 }} exit={{ opacity: 0, y: -16 }} className="profile-page__toast--success">
          <FiCheck size={14}/>
          {docUploadToast}
        </motion.div>)}

      
      {docUploadError && (<motion.div initial={{ opacity: 0, y: -16, x: 16 }} animate={{ opacity: 1, y: 0, x: 0 }} exit={{ opacity: 0, y: -16 }} className="profile-page__toast--error">
          <FiAlertTriangle size={14}/>
          {docUploadError}
        </motion.div>)}

      
      {docDeleteToast && (<motion.div initial={{ opacity: 0, y: -16, x: 16 }} animate={{ opacity: 1, y: 0, x: 0 }} exit={{ opacity: 0, y: -16 }} className="profile-page__toast--success">
          <FiCheck size={14}/>
          {docDeleteToast}
        </motion.div>)}

      
      {docDeleteError && (<motion.div initial={{ opacity: 0, y: -16, x: 16 }} animate={{ opacity: 1, y: 0, x: 0 }} exit={{ opacity: 0, y: -16 }} className="profile-page__toast--error">
          <FiAlertTriangle size={14}/>
          {docDeleteError}
        </motion.div>)}

      
      {docMemoryToast && (<motion.div initial={{ opacity: 0, y: -16, x: 16 }} animate={{ opacity: 1, y: 0, x: 0 }} exit={{ opacity: 0, y: -16 }} className="profile-page__toast--success">
          <FiCheck size={14}/>
          {docMemoryToast}
        </motion.div>)}

      
      {docMemoryError && (<motion.div initial={{ opacity: 0, y: -16, x: 16 }} animate={{ opacity: 1, y: 0, x: 0 }} exit={{ opacity: 0, y: -16 }} className="profile-page__toast--error">
          <FiAlertTriangle size={14}/>
          {docMemoryError}
        </motion.div>)}

      
      {docDeleteConfirmId && (<div className="voice-modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget)
            setDocDeleteConfirmId(null); }}>
          <motion.div className="voice-modal profile-page__modal--sm" initial={{ opacity: 0, scale: 0.95, y: 16 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 16 }} transition={{ type: 'spring', stiffness: 360, damping: 30 }}>
            <div className="voice-modal__header">
              <div className="profile-page__modal-header-row">
                <div className="profile-page__modal-icon-badge profile-page__modal-icon-badge--danger">
                  <FiTrash2 size={15}/>
                </div>
                <div>
                  <div className="voice-modal__title">Delete Document</div>
                  <div className="profile-page__modal-subtext">This action cannot be undone</div>
                </div>
              </div>
              <button className="voice-modal__close" onClick={() => setDocDeleteConfirmId(null)}><FiX size={16}/></button>
            </div>

            <div className="profile-page__modal-body">
              <p className="profile-page__modal-body-text">
                Are you sure you want to delete "<strong>{uploadedDocs.find(d => d.transactionId === docDeleteConfirmId)?.fileName}</strong>"? It will be permanently removed.
              </p>
            </div>

            <div className="profile-page__modal-footer-row">
              <button className="api-key-btn-cancel" onClick={() => setDocDeleteConfirmId(null)}>Cancel</button>
              <button className="api-key-btn-confirm profile-page__delete-confirm-btn" onClick={() => deleteDoc(docDeleteConfirmId, uploadedDocs.find(d => d.transactionId === docDeleteConfirmId)?.fileName)}>
                <FiTrash2 size={12}/> Delete
              </button>
            </div>
          </motion.div>
        </div>)}

      
      {showCreateModal && (<div className="voice-modal-backdrop" onClick={(e) => e.target === e.currentTarget && !createdKey && handleCloseCreateModal()}>
          <motion.div className="voice-modal profile-page__modal--md" initial={{ opacity: 0, scale: 0.95, y: 16 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 16 }} transition={{ type: 'spring', stiffness: 360, damping: 30 }}>
            {!createdKey ? (<>
                <div className="voice-modal__header">
                  <div className="profile-page__modal-header-row">
                    <div className="profile-page__modal-icon-badge profile-page__modal-icon-badge--purple">
                      <FiLock size={15}/>
                    </div>
                    <div>
                      <div className="voice-modal__title">Create API Key</div>
                      <div className="profile-page__modal-subtext">
                        Give this key a name to identify it later
                      </div>
                    </div>
                  </div>
                  <button className="voice-modal__close" onClick={handleCloseCreateModal}>
                    <FiX size={16}/>
                  </button>
                </div>

                <div className="profile-page__modal-field-wrap">
                  <label className="profile-field__label profile-page__modal-field-label">
                    Key Name
                  </label>
                  <input className="glass-input profile-page__modal-input" value={newKeyName} onChange={(e) => setNewKeyName(e.target.value)} placeholder="e.g. production-app" autoFocus onKeyDown={(e) => e.key === 'Enter' && !createLoading && handleCreateKey()}/>
                </div>

                {modalError && (<div className="profile-page__modal-error-banner">
                    <span className="profile-page__modal-error-icon">⚠</span>
                    <span>{modalError}</span>
                  </div>)}

                <div className="voice-modal__footer">
                  <button className="glass-btn" onClick={handleCloseCreateModal}>Cancel</button>
                  <button className="glass-btn profile-page__purple-btn" onClick={handleCreateKey} disabled={createLoading || !newKeyName.trim()} style={{ opacity: createLoading || !newKeyName.trim() ? 0.6 : 1 }}>
                    {createLoading ? (<><span className="profile-loader profile-loader--md"/> Creating…</>) : 'Create Key'}
                  </button>
                </div>
              </>) : (<>
                <div className="voice-modal__header">
                  <div className="profile-page__modal-header-row">
                    <div className="profile-page__modal-icon-badge profile-page__modal-icon-badge--green">
                      <FiCheck size={15}/>
                    </div>
                    <div>
                      <div className="voice-modal__title">Key Created</div>
                      <div className="profile-page__modal-subtext">
                        Copy and save it — you won't see this again
                      </div>
                    </div>
                  </div>
                </div>

                <div className="profile-page__key-created-wrap">
                  <div className="profile-page__key-display-row">
                    <code className="profile-page__key-code">
                      {createdKey}
                    </code>
                    <button onClick={handleCopyKey} className="profile-page__copy-key-btn" style={{
                    background: isCopied ? 'rgba(52,211,153,0.12)' : 'rgba(34,211,238,0.1)',
                    border: `1px solid ${isCopied ? 'rgba(52,211,153,0.3)' : 'rgba(34,211,238,0.25)'}`,
                    color: isCopied ? 'var(--accent-green, #34d399)' : 'var(--accent-cyan)',
                }}>
                      {isCopied ? <FiCheck size={12}/> : <FiCopy size={12}/>}
                      {isCopied ? 'Copied' : 'Copy'}
                    </button>
                  </div>

                  <div className="profile-page__key-warning-box">
                    <FiAlertTriangle size={13} style={{ color: 'var(--accent-yellow, #fbbf24)', flexShrink: 0, marginTop: 1 }}/>
                    <span className="profile-page__key-warning-text">
                      Store this key securely. It will not be shown again after you close this dialog.
                    </span>
                  </div>
                </div>

                <div className="voice-modal__footer">
                  <button className="glass-btn profile-page__purple-btn" onClick={handleCloseCreateModal}>
                    I've saved it
                  </button>
                </div>
              </>)}
          </motion.div>
        </div>)}
      
      {viewDoc && (<FilePreviewModal name={viewDoc.fileName} url={viewDoc.fileUrl!} onClose={() => setViewDoc(null)}/>)}
    </div>);
};
export default ProfilePage;
